/**
 * StrictSearch — moteur de recherche Starcow
 *
 * - Interroge la Storefront API avec une requête par champs (titre / vendor / type / tag),
 *   ce qui désactive la recherche sémantique de Shopify.
 * - Tri par nouveautés (createdAt desc) côté Shopify, pagination par curseur.
 * - Garde-fou côté client : chaque mot de la recherche doit être présent dans les champs
 *   visibles du produit (titre, vendor, type, tags, metafield custom.brand).
 * - Même règle que le thème pour les produits "raffle" (product.real_product + product.countdown).
 *
 * Configuration attendue dans window.strictSearchConfig (voir layout/theme.liquid) :
 *   { token, enabled, country, language, moneyFormat, rootUrl, strings: {...}, closeIcon }
 */
(function () {
  'use strict';

  var API_VERSION = '2026-07';
  var STOPWORDS = ['de', 'du', 'des', 'la', 'le', 'les', 'un', 'une', 'et', 'en', 'pour', 'the', 'for', 'and', 'a', 'of'];

  function normalize(s) {
    return String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function tokenize(q) {
    return normalize(q).split(' ').filter(function (t) {
      return t && STOPWORDS.indexOf(t) === -1;
    });
  }

  /**
   * "jordan 4 bred" -> (title:jordan OR …) AND (title:4 OR …) AND (title:bred* OR …)
   * availability : undefined = tous, true = en stock seulement, false = épuisés seulement
   */
  function buildQuery(tokens, availability) {
    var q = tokens
      .map(function (t, i) {
        var prefix = i === tokens.length - 1 && t.length >= 3;
        var v = prefix ? t + '*' : t;
        return '(title:' + v + ' OR vendor:' + v + ' OR product_type:' + v + ' OR tag:' + t + ')';
      })
      .join(' AND ');
    if (availability === true) q += ' AND available_for_sale:true';
    if (availability === false) q += ' AND available_for_sale:false';
    return q;
  }

  /** Vérification stricte côté client. */
  function matches(p, tokens) {
    var hay =
      ' ' +
      normalize(
        [p.title, p.vendor, p.productType, (p.tags || []).join(' '), p.brand && p.brand.value].join(' ')
      ) +
      ' ';
    return tokens.every(function (t, i) {
      var prefix = i === tokens.length - 1 && t.length >= 3;
      return prefix ? hay.indexOf(' ' + t) !== -1 : hay.indexOf(' ' + t + ' ') !== -1;
    });
  }

  /** Produit "raffle" masqué une fois le countdown passé (même règle que main-search.liquid). */
  function isVisible(p, now) {
    now = now || Date.now();
    if (!p.realProduct || p.realProduct.value !== 'true') return true;
    if (!p.countdown || !p.countdown.value) return false;
    return new Date(p.countdown.value).getTime() > now;
  }

  var QUERY = [
    'query StrictSearch($q: String!, $first: Int!, $after: String, $country: CountryCode, $language: LanguageCode)',
    '@inContext(country: $country, language: $language) {',
    '  products(first: $first, after: $after, query: $q, sortKey: CREATED_AT, reverse: true) {',
    '    pageInfo { hasNextPage endCursor }',
    '    nodes {',
    '      id handle title vendor productType tags createdAt availableForSale',
    '      priceRange { minVariantPrice { amount currencyCode } }',
    '      compareAtPriceRange { maxVariantPrice { amount currencyCode } }',
    '      images(first: 2) { nodes { url(transform: { maxWidth: 900 }) altText } }',
    '      brand: metafield(namespace: "custom", key: "brand") { value }',
    '      realProduct: metafield(namespace: "product", key: "real_product") { value }',
    '      countdown: metafield(namespace: "product", key: "countdown") { value }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  function Engine(config) {
    this.token = config.token;
    this.country = (config.country || 'FR').toUpperCase();
    this.language = (config.language || 'fr').split('-')[0].toUpperCase();
    this.cache = {};
  }

  Engine.prototype.fetchBatch = function (q, first, after) {
    var self = this;
    return fetch('/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': self.token,
      },
      body: JSON.stringify({
        query: QUERY,
        variables: { q: q, first: first, after: after, country: self.country, language: self.language },
      }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (json.errors) {
          throw new Error(
            json.errors
              .map(function (e) {
                return e.message;
              })
              .join(' | ')
          );
        }
        return json.data.products;
      });
  };

  /**
   * Renvoie une page de `pageSize` produits strictement pertinents, triés par nouveauté.
   * Comme le post-filtre peut écarter des produits, on enchaîne les lots jusqu'à remplir la page.
   *
   * Gestion des épuisés (config.soldOut) :
   *   'show' : mélangés aux autres, tri nouveautés pur
   *   'hide' : exclus (available_for_sale:true)
   *   'end'  : phase 1 = en stock par nouveautés, phase 2 = épuisés par nouveautés
   * Le curseur renvoyé encode la phase : "<phase>|<endCursor>".
   */
  Engine.prototype.search = function (rawQuery, opts) {
    opts = opts || {};
    var pageSize = opts.pageSize || 30;
    var mode = (window.strictSearchConfig && window.strictSearchConfig.soldOut) || 'show';
    var tokens = tokenize(rawQuery);
    var self = this;

    if (!tokens.length) {
      return Promise.resolve({ items: [], cursor: null, hasMore: false, tokens: tokens });
    }

    var phase = 0;
    var after = null;
    if (opts.cursor) {
      var parts = String(opts.cursor).split('|');
      phase = parseInt(parts[0], 10) || 0;
      after = parts.slice(1).join('|') || null;
    }

    var phases;
    if (mode === 'hide') phases = [true];
    else if (mode === 'end') phases = [true, false];
    else phases = [undefined];

    var cacheKey = tokens.join(' ') + '|' + mode + '|' + pageSize + '|' + (opts.cursor || '');
    if (self.cache[cacheKey]) return Promise.resolve(self.cache[cacheKey]);

    var items = [];
    var batches = 0;

    function finish(hasMore) {
      var result = {
        items: items,
        cursor: hasMore ? phase + '|' + (after || '') : null,
        hasMore: hasMore,
        tokens: tokens,
      };
      self.cache[cacheKey] = result;
      return result;
    }

    function step() {
      if (phase >= phases.length) return finish(false);
      batches += 1;
      var q = buildQuery(tokens, phases[phase]);
      return self.fetchBatch(q, Math.min(250, pageSize * 2), after).then(function (page) {
        page.nodes.forEach(function (p) {
          if (matches(p, tokens) && isVisible(p)) items.push(p);
        });
        after = page.pageInfo.endCursor;

        if (!page.pageInfo.hasNextPage) {
          // phase épuisée : on passe à la suivante
          phase += 1;
          after = null;
          if (phase >= phases.length) return finish(false);
          if (items.length >= pageSize) return finish(true);
          return batches < 6 ? step() : finish(true);
        }
        if (items.length < pageSize && batches < 6) return step();
        return finish(true);
      });
    }

    return step();
  };

  /* ------------------------------------------------------------------ */
  /* Helpers de rendu                                                     */
  /* ------------------------------------------------------------------ */

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function numericId(gid) {
    return String(gid || '').split('/').pop();
  }

  function productUrl(p) {
    var root = (window.strictSearchConfig && window.strictSearchConfig.rootUrl) || '/';
    if (root.slice(-1) !== '/') root += '/';
    return root + 'products/' + p.handle;
  }

  function formatMoney(amount, currency) {
    var format = (window.strictSearchConfig && window.strictSearchConfig.moneyFormat) || '{{amount}} €';
    var value = parseFloat(amount || 0);
    function fixed(n, dec, thousands, decimal) {
      var parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    var map = {
      amount: fixed(value, 2, ',', '.'),
      amount_no_decimals: fixed(value, 0, ',', '.'),
      amount_with_comma_separator: fixed(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fixed(value, 0, '.', ','),
      amount_with_space_separator: fixed(value, 2, ' ', ','),
      amount_no_decimals_with_space_separator: fixed(value, 0, ' ', ','),
      amount_with_apostrophe_separator: fixed(value, 2, "'", '.'),
    };
    var out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      return map[key] != null ? map[key] : map.amount;
    });
    if (!/\{\{/.test(format) && !out) out = map.amount + ' ' + (currency || '');
    return out;
  }

  function daysSince(iso) {
    return (Date.now() - new Date(iso).getTime()) / 86400000;
  }

  function isInstoreOnly(p) {
    return (p.tags || []).indexOf('instore-only') !== -1;
  }

  function unavailableLabel(p, strings) {
    return isInstoreOnly(p) ? strings.instoreOnly || strings.soldOut : strings.soldOut;
  }

  function badgeFor(p, strings) {
    var price = parseFloat(p.priceRange.minVariantPrice.amount);
    var compare = parseFloat((p.compareAtPriceRange && p.compareAtPriceRange.maxVariantPrice.amount) || 0);
    var isFictif = p.realProduct && p.realProduct.value === 'true';
    var countdown = p.countdown && p.countdown.value ? new Date(p.countdown.value) : null;

    if (isFictif && countdown && countdown.getTime() > Date.now()) {
      var d = countdown;
      var pad = function (n) {
        return (n < 10 ? '0' : '') + n;
      };
      var when =
        (window.strictSearchConfig.language || 'fr').indexOf('fr') === 0
          ? pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + ' à ' + pad(d.getHours()) + 'h' + pad(d.getMinutes())
          : pad(d.getMonth() + 1) + '/' + pad(d.getDate()) + ' at ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
      return (
        '<div class="c-badge | u-mt-10 u-ml-10 | u-absolute"><span class="t-h4">Coming Soon</span></div>' +
        '<div class="c-badge | u-ml-10 u-mt-40 | u-absolute" style="top: 30px; background-color:rgb(68, 68, 68)"><span class="t-h4">' +
        escapeHtml(when) +
        '</span></div>'
      );
    }
    if (!isFictif && !p.availableForSale) {
      return '<div class="c-badge | u-mt-10 u-ml-10 | u-absolute"><span class="t-h4">' + escapeHtml(unavailableLabel(p, strings)) + '</span></div>';
    }
    if (compare > price) {
      var pct = Math.round(((compare - price) * 100) / compare);
      return '<div class="c-badge | u-mt-10 u-ml-10 | u-absolute"><span class="t-h4">Promo -' + pct + '%</span></div>';
    }
    if (daysSince(p.createdAt) < 30) {
      return '<div class="c-badge | u-mt-10 u-ml-10 | u-absolute"><span class="t-h4">New</span></div>';
    }
    var tagBadge = null;
    (p.tags || []).forEach(function (tag) {
      if (tag.indexOf('badge_') !== -1) tagBadge = tag.replace('badge_', '').replace(/-/g, ' ');
    });
    if (tagBadge) {
      return '<div class="c-badge | u-mt-10 u-ml-10 | u-absolute"><span class="t-h4">' + escapeHtml(tagBadge) + '</span></div>';
    }
    return '';
  }

  /** Carte produit, calquée sur snippets/card-product.liquid */
  function renderCard(p) {
    var cfg = window.strictSearchConfig || {};
    var strings = cfg.strings || {};
    var id = numericId(p.id);
    var url = productUrl(p);
    var images = (p.images && p.images.nodes) || [];
    var price = p.priceRange.minVariantPrice;
    var compare = p.compareAtPriceRange && p.compareAtPriceRange.maxVariantPrice;
    var onSale = compare && parseFloat(compare.amount) > parseFloat(price.amount);
    var isFictif = p.realProduct && p.realProduct.value === 'true';
    var brand = (p.brand && p.brand.value) || p.vendor || '';
    var shopName = cfg.shopName || '';
    var formId = 'quick-add-strict' + id;

    var html = '';
    html += '<li class="u-col-span-6 lg:u-col-span-3 u-mb-20 md:u-mb-40 3xl:u-mb-40-fluid-height">';
    html += '<div class="c-card-product | u-relative | u-h-fit" itemscope itemtype="http://schema.org/Product">';
    html += '<div class="u-flex u-flex-col u-justify-between">';
    html += '<div class="u-relative | u-bg-grey-light | u-rounded">';
    html += badgeFor(p, strings);
    html += '<figure class="c-card-product__thumbnail | u-aspect-w-4 u-aspect-h-5">';
    if (images[0]) {
      html +=
        '<img data-src="' + escapeHtml(images[0].url) + '" class="' + (images[1] ? 'c-card-product__thumbnail__img ' : '') +
        '| u-object-contain | u-rounded | lazyload" itemprop="image" alt="' + escapeHtml((images[0].altText || p.title) + ' - ' + shopName + ' - 1') + '"/>';
    }
    if (images[1]) {
      html +=
        '<img data-src="' + escapeHtml(images[1].url) + '" class="c-card-product__thumbnail__img--hover | u-object-contain | u-rounded | lazyload" itemprop="image" alt="' +
        escapeHtml((images[1].altText || p.title) + ' - ' + shopName + ' - 2') + '"/>';
    }
    html += '</figure>';

    if (!isFictif) {
      html += '<div class="c-card-product__quick-buy u-px-10 3xl:u-px-10-fluid-width u-w-full u-z-2 u-none md:u-block">';
      if (p.availableForSale) {
        html += '<modal-opener data-modal="#QuickAdd-' + id + '">';
        html +=
          '<button id="' + formId + '-submit" type="submit" name="add" class="quick-add__submit button t-button t-button--quickbuy u-rounded u-w-full u-pointer-events-auto" aria-haspopup="dialog" data-product-url="' +
          escapeHtml(url) + '">';
        html += '<span class="t-h4" style="font-size: clamp(10px, calc(8px + 0.35vw), 14px);">' + escapeHtml(strings.addToCart) + '</span>';
        html += '<div class="loading-overlay__spinner hidden"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';
        html += '</button></modal-opener>';
      } else {
        html +=
          '<button class="quick-add__submit button t-button u-rounded u-w-full u-pointer-events-none !u-mb-0" data-product-url="' + escapeHtml(url) + '">' +
          '<span class="t-h4" style="font-size: clamp(10px, calc(8px + 0.35vw), 14px);">' + escapeHtml(unavailableLabel(p, strings)) + '</span></button>';
      }
      html += '</div>';
    }
    html += '</div>'; // image wrapper

    html += '<div class="u-mt-15 3xl:u-mt-15-fluid-height">';
    html += '<span class="t-surtitle | u-block">' + escapeHtml(brand) + '</span>';
    html +=
      '<a href="' + escapeHtml(url) + '" class="u-block | u-text-14 3xl:u-text-[0.972vw] u-font-medium | u-extend-href" style="line-height:100%!important; margin:5px 0;">' +
      escapeHtml(p.title) + '</a>';
    html += '<div class="u-flex">';
    html += '<p class="t-surtitle t-surtitle--price"' + (onSale ? ' style="color: #c50000;"' : '') + '>' + escapeHtml(formatMoney(price.amount, price.currencyCode)) + '</p>';
    if (onSale) {
      html += '<del class="t-surtitle t-surtitle--price | u-ml-5 3xl:u-ml-5-fluid-width">' + escapeHtml(formatMoney(compare.amount, compare.currencyCode)) + '</del>';
    }
    html += '</div></div>';
    html += '</div></div>'; // flex col + card

    if (!isFictif) {
      html += '<quick-add-modal id="QuickAdd-' + id + '" class="quick-add-modal l-sidebar-quickbuy l-sidebar-quickbuy--right js-quickbuy-init">';
      html += '<span id="ModalClose-' + id + '" class="l-sidebar-quickbuy__overlay u-block"></span>';
      html += '<div role="dialog" aria-label="' + escapeHtml(p.title) + '" aria-modal="true" class="quick-add-modal__content global-settings-popup l-sidebar-quickbuy__wrapper" tabindex="-1">';
      html += '<button id="ModalClose-' + id + '" type="button" class="l-sidebar-quickbuy__close" aria-label="' + escapeHtml(strings.close) + '">' + (cfg.closeIcon || '') + '</button>';
      html += '<div id="QuickAddInfo-' + id + '" class="quick-add-modal__content-info l-sidebar-quickbuy__inner"></div>';
      html += '</div></quick-add-modal>';
    }
    html += '</li>';
    return html;
  }

  /** Ligne produit pour la recherche prédictive (calquée sur sections/predictive-search.liquid) */
  function renderPredictiveItem(p, index) {
    var cfg = window.strictSearchConfig || {};
    var url = productUrl(p);
    var images = (p.images && p.images.nodes) || [];
    var brand = (p.brand && p.brand.value) || p.vendor || '';
    var price = p.priceRange.minVariantPrice;
    var compare = p.compareAtPriceRange && p.compareAtPriceRange.maxVariantPrice;
    var onSale = compare && parseFloat(compare.amount) > parseFloat(price.amount);

    var html = '<li id="predictive-search-option-' + (index + 1) + '" role="option" aria-selected="false" class="u-relative">';
    html += '<a href="' + escapeHtml(url) + '" tabindex="-1" class="u-flex u-items-center u-gap-x-15 3xl:u-gap-x-15-fluid-height u-extend-href">';
    if (images[0]) {
      html += '<div class="u-w-[82px] 3xl:u-w-[5.694vw] | u-shrink-0"><img src="' + escapeHtml(images[0].url) + '" alt="' + escapeHtml(p.title) + '" class="u-rounded u-w-full u-object-contain" loading="lazy" width="82" height="103"/></div>';
    }
    html += '<div><div class="t-surtitle">' + escapeHtml(brand) + '</div>';
    html += '<div class="t-text | u-mb-5 3xl:u-mb-5-fluid-height">' + escapeHtml(p.title) + '</div>';
    if (cfg.predictiveShowPrice) {
      html += '<span class="t-surtitle u-font-bold">' + escapeHtml(formatMoney(price.amount, price.currencyCode));
      if (onSale) html += ' <del>' + escapeHtml(formatMoney(compare.amount, compare.currencyCode)) + '</del>';
      html += '</span>';
    }
    html += '</div></a></li>';
    return html;
  }

  /* ------------------------------------------------------------------ */
  /* Custom element : page de résultats                                   */
  /* ------------------------------------------------------------------ */

  function getEngine() {
    var cfg = window.strictSearchConfig;
    if (!cfg || !cfg.enabled || !cfg.token) return null;
    if (!window.__strictSearchEngine) window.__strictSearchEngine = new Engine(cfg);
    return window.__strictSearchEngine;
  }

  if (window.customElements && !customElements.get('strict-search-results')) {
    customElements.define(
      'strict-search-results',
      class extends HTMLElement {
        connectedCallback() {
          this.query = this.getAttribute('data-query') || '';
          this.pageSize = parseInt(this.getAttribute('data-page-size'), 10) || 30;
          this.grid = this.querySelector('[data-strict-grid]');
          this.status = this.querySelector('[data-strict-status]');
          this.moreBtn = this.querySelector('[data-strict-more]');
          this.countEl = document.querySelector('[data-strict-count]');
          this.fallback = document.querySelector('[data-strict-fallback]');
          this.cursor = null;
          this.total = 0;
          this.engine = getEngine();

          if (!this.engine) return this.useFallback();

          if (this.moreBtn) this.moreBtn.addEventListener('click', this.loadMore.bind(this));
          this.loadMore();
        }

        /** Squelettes : rendus côté serveur au premier chargement, réinjectés à chaque "Voir plus". */
        skeletonMarkup() {
          if (this._skeleton === undefined) {
            var first = this.grid.querySelector('.c-card-skeleton');
            this._skeleton = first ? first.outerHTML : '';
          }
          return this._skeleton;
        }

        showSkeleton(count) {
          var one = this.skeletonMarkup();
          if (!one) return;
          var html = '';
          for (var i = 0; i < count; i++) html += one;
          this.grid.insertAdjacentHTML('beforeend', html);
        }

        clearSkeleton() {
          this.skeletonMarkup(); // mémorise le markup avant suppression
          this.grid.querySelectorAll('.c-card-skeleton').forEach(function (el) {
            el.remove();
          });
        }

        setLoading(on) {
          this.classList.toggle('is-loading', on);
          if (this.moreBtn) this.moreBtn.disabled = on;
          if (on && this.total > 0) this.showSkeleton(Math.min(this.pageSize, 10));
          if (!on) this.clearSkeleton();
        }

        loadMore() {
          var self = this;
          self.setLoading(true);
          self.engine
            .search(self.query, { pageSize: self.pageSize, cursor: self.cursor })
            .then(function (res) {
              self.cursor = res.cursor;
              self.total += res.items.length;
              self.clearSkeleton();
              if (res.items.length) {
                self.grid.insertAdjacentHTML('beforeend', res.items.map(renderCard).join(''));
                if (window.lazySizes && typeof window.lazySizes.loader !== 'undefined') window.lazySizes.loader.checkElems();
              }
              if (self.countEl) self.countEl.textContent = self.total + (res.hasMore ? '+' : '');
              if (self.moreBtn) self.moreBtn.hidden = !res.hasMore;
              if (self.total === 0) self.useFallback(true);
              self.dispatchEvent(new CustomEvent('strict-search:rendered', { bubbles: true, detail: res }));
            })
            .catch(function (err) {
              console.error('[StrictSearch]', err);
              self.useFallback();
            })
            .finally(function () {
              self.setLoading(false);
            });
        }

        /** Affiche les résultats natifs rendus côté serveur (0 résultat strict, ou erreur API). */
        useFallback(noResults) {
          var strings = (window.strictSearchConfig && window.strictSearchConfig.strings) || {};
          this.hidden = true;
          if (this.fallback) {
            this.fallback.hidden = false;
            var label = this.fallback.querySelector('[data-strict-fallback-label]');
            if (label && noResults) {
              label.textContent = strings.similarResults || '';
              label.hidden = false;
            }
          }
          if (this.countEl && this.countEl.getAttribute('data-native-count') != null) {
            this.countEl.textContent = this.countEl.getAttribute('data-native-count');
          }
        }
      }
    );
  }

  window.StrictSearch = {
    Engine: Engine,
    getEngine: getEngine,
    normalize: normalize,
    tokenize: tokenize,
    buildQuery: buildQuery,
    matches: matches,
    isVisible: isVisible,
    renderCard: renderCard,
    renderPredictiveItem: renderPredictiveItem,
    formatMoney: formatMoney,
  };
})();
